import axios from 'axios';
import useSWR from 'swr';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Form, message } from 'antd';
import { useLocation } from 'react-router-dom';
import dayjs from 'dayjs';

interface BrandType {
    code: string;
    description: string;
}

interface ModelType {
    code: string;
    description: string;
}

interface ProvinceType {
    kode_provinsi: string;
    nama_provinsi: string;
}

interface RegencyType {
    kode_dati: string;
    deskripsi_kode_dati: string;
}

interface DistrictType {
    kode_kecamatan: string;
    deskripsi_kode_kecamatan: string;
}

interface VillageType {
    kode_kelurahan: string;
    deskripsi_kode_kelurahan: string;
    kode_pos: string;
}

export interface CustomerDataType {
    key: string;
    cs_code: string;
    cs_name: string;
    customer_type: string;
    address_line1: string;
    address_line2: string;
    id_no?: string;
    provinsi?: string;
    kabupaten?: string;
    kecamatan?: string;
    kelurahan?: string;
    kode_pos?: string;
    no_hp1?: string;
}

const fetcher = (url: string) => axios.get(url).then(res => res.data);

export const useVehicleMasterData = (
    provinceCode?: string | null,
    regencyCode?: string | null,
    districtCode?: string | null
) => {

    const { data: brands, error: brandsError, isLoading: isBrandsLoading } = useSWR<BrandType[]>(
        `${process.env.REACT_APP_BACKEND_URL}/api/vehicle-master/brands`,
        fetcher
    );

    const { data: models, error: modelsError, isLoading: isModelsLoading } = useSWR<ModelType[]>(
        `${process.env.REACT_APP_BACKEND_URL}/api/vehicle-master/models`,
        fetcher
    );

    const { data: provinces, error: provincesError, isLoading: isProvincesLoading } = useSWR<ProvinceType[]>(
        `${process.env.REACT_APP_BACKEND_URL}/api/vehicle-master/provinces`,
        fetcher
    );

    const { data: regencies, error: regenciesError, isLoading: isRegenciesLoading } = useSWR<RegencyType[]>(
        provinceCode ? `${process.env.REACT_APP_BACKEND_URL}/api/vehicle-master/regencies/${provinceCode}` : null,
        fetcher
    );

    const { data: districts, error: districtsError, isLoading: isDistrictsLoading } = useSWR<DistrictType[]>(
        regencyCode ? `${process.env.REACT_APP_BACKEND_URL}/api/vehicle-master/districts/${regencyCode}` : null,
        fetcher
    );

    const { data: villages, error: villagesError, isLoading: isVillagesLoading } = useSWR<VillageType[]>(
        districtCode ? `${process.env.REACT_APP_BACKEND_URL}/api/vehicle-master/villages/${districtCode}` : null,
        fetcher
    );

    return {
        brands,
        brandsError,
        isBrandsLoading,
        models,
        modelsError,
        isModelsLoading,
        provinces,
        provincesError,
        isProvincesLoading,
        regencies,
        regenciesError,
        isRegenciesLoading,
        districts,
        districtsError,
        isDistrictsLoading,
        villages,
        villagesError,
        isVillagesLoading,
    };
};


export const useVehicleMasterLogic = () => {
    const [form] = Form.useForm();
    const location = useLocation();
    const vehicleIdFromState = location.state?.vehicleId;
    const [selectedProvince, setSelectedProvince] = useState<string | null>(null);
    const [selectedRegency, setSelectedRegency] = useState<string | null>(null);
    const [selectedDistrict, setSelectedDistrict] = useState<string | null>(null);
    const [selectedProvinceBilling, setSelectedProvinceBilling] = useState<string | null>(null);
    const [selectedRegencyBilling, setSelectedRegencyBilling] = useState<string | null>(null);
    const [selectedDistrictBilling, setSelectedDistrictBilling] = useState<string | null>(null);
    const [selectedProvinceStnk, setSelectedProvinceStnk] = useState<string | null>(null);
    const [selectedRegencyStnk, setSelectedRegencyStnk] = useState<string | null>(null);
    const [selectedDistrictStnk, setSelectedDistrictStnk] = useState<string | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalTarget, setModalTarget] = useState<'currentUser' | 'billing' | null>(null);
    const [autosaveStatus, setAutosaveStatus] = useState<'idle' | 'processing' | 'error' | 'stopped'>('stopped');
    const [vehicleId, setVehicleId] = useState<number | null>(null);
    const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
    const [showStnk, setShowStnk] = useState(false);
    const [showCurrentUser, setShowCurrentUser] = useState(false);
    const [showBilling, setShowBilling] = useState(false);

    const { data: vehicleData } = useSWR(
        vehicleIdFromState ? `${process.env.REACT_APP_BACKEND_URL}/api/vehicle-master/vehicle/${vehicleIdFromState}` : null,
        fetcher
    );

    useEffect(() => {
        if (vehicleData) {

            setVehicleId(vehicleData.id);
            if (vehicleData.provinsi_current_user) setSelectedProvince(vehicleData.provinsi_current_user);
            if (vehicleData.kabupaten_current_user) setSelectedRegency(vehicleData.kabupaten_current_user);
            if (vehicleData.kecamatan_current_user) setSelectedDistrict(vehicleData.kecamatan_current_user);
            if (vehicleData.provinsi_billing) setSelectedProvinceBilling(vehicleData.provinsi_billing);
            if (vehicleData.kabupaten_billing) setSelectedRegencyBilling(vehicleData.kabupaten_billing);
            if (vehicleData.kecamatan_billing) setSelectedDistrictBilling(vehicleData.kecamatan_billing);
            if (vehicleData.provinsi_stnk) setSelectedProvinceStnk(vehicleData.provinsi_stnk);
            if (vehicleData.kabupaten_stnk) setSelectedRegencyStnk(vehicleData.kabupaten_stnk);
            if (vehicleData.kecamatan_stnk) setSelectedDistrictStnk(vehicleData.kecamatan_stnk);

            const formData = {
                ...vehicleData,
                status: vehicleData.status === '1' ? 'Active' : 'Deactive',
                date: vehicleData.date ? dayjs(vehicleData.date) : null,
            };
            form.setFieldsValue(formData);
            setAutosaveStatus('stopped');
        } else if (!vehicleIdFromState) {

            form.setFieldsValue({ status: 'Active' });
        }
    }, [vehicleData, vehicleIdFromState, form]);


    const {
        brands,
        isBrandsLoading,
        models,
        isModelsLoading,
        provinces,
        isProvincesLoading,
        regencies,
        isRegenciesLoading,
        districts,
        isDistrictsLoading,
        villages,
        isVillagesLoading
    } = useVehicleMasterData(selectedProvince, selectedRegency, selectedDistrict);

    const {
        regencies: regenciesBilling,
        isRegenciesLoading: isRegenciesLoadingBilling,
        districts: districtsBilling,
        isDistrictsLoading: isDistrictsLoadingBilling,
        villages: villagesBilling,
        isVillagesLoading: isVillagesLoadingBilling
    } = useVehicleMasterData(selectedProvinceBilling, selectedRegencyBilling, selectedDistrictBilling);

    const {
        regencies: regenciesStnk,
        isRegenciesLoading: isRegenciesLoadingStnk,
        districts: districtsStnk,
        isDistrictsLoading: isDistrictsLoadingStnk,
        villages: villagesStnk,
        isVillagesLoading: isVillagesLoadingStnk
    } = useVehicleMasterData(selectedProvinceStnk, selectedRegencyStnk, selectedDistrictStnk);

    const handleFormChange = useCallback((changedValues: any, allValues: any) => {
        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }

        const engineNo = allValues.vehicle_engine_no;
        if (!engineNo || String(engineNo).trim() === '') {
            setAutosaveStatus('stopped');
            return;
        }

        setAutosaveStatus('processing');

        debounceTimeout.current = setTimeout(async () => {
            try {
                const payload = { ...allValues, id: vehicleId };

                if (payload.status === 'Active') {
                    payload.status = '1';
                } else if (payload.status === 'Deactive') {
                    payload.status = '2';
                }

                if (payload.date && typeof payload.date.format === 'function') {
                    payload.date = payload.date.format('YYYY-MM-DD');
                }

                const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/vehicle-master/vehicle`, payload);

                if (response.data && response.data.id) {
                    if (!vehicleId) {
                        setVehicleId(response.data.id);
                    }
                }
                setAutosaveStatus('idle');
            } catch (error) {
                console.error("Autosave failed:", error);
                setAutosaveStatus('error');
            }
        }, 1500);

    }, [vehicleId]);

    const showModal = (target: 'currentUser' | 'billing') => {
        setModalTarget(target);
        setIsModalOpen(true);
    };

    const handleOk = () => {
        setIsModalOpen(false);
        setModalTarget(null);
    };

    const handleCancel = () => {
        setIsModalOpen(false);
        setModalTarget(null);
    };

    const handleDeactivateToggle = async () => {
        if (!vehicleId) {
            message.warning('Simpan data kendaraan terlebih dahulu (dengan mengisi No Mesin) sebelum mengubah status.');
            return;
        }

        const currentStatus = form.getFieldValue('status');
        const newStatus = currentStatus === 'Active' ? 'Deactive' : 'Active';
        const newStatusValue = newStatus === 'Active' ? '1' : '2';

        try {
            message.loading({ content: 'Memperbarui status...', key: 'statusUpdate' });
            await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/vehicle-master/vehicle/${vehicleId}/status`, {
                status: newStatusValue,
            });

            form.setFieldsValue({ status: newStatus });
            message.success({ content: 'Status berhasil diperbarui!', key: 'statusUpdate', duration: 2 });
        } catch (error) {
            console.error("Gagal memperbarui status:", error);
            message.error({ content: 'Gagal memperbarui status. Silakan coba lagi.', key: 'statusUpdate', duration: 2 });
        }
    }

    const handleShowStnkToggle = () => setShowStnk(prev => !prev);
    const handleShowCurrentUserToggle = () => setShowCurrentUser(prev => !prev);
    const handleShowBillingToggle = () => setShowBilling(prev => !prev);

    const handleProvinceChange = (value: string) => {
        setSelectedProvince(value);
        form.setFieldsValue({ kabupaten_current_user: null, kecamatan_current_user: null, kelurahan_current_user: null });
        setSelectedRegency(null);
        setSelectedDistrict(null);
    };

    const handleRegencyChange = (value: string) => {
        setSelectedRegency(value);
        form.setFieldsValue({ kecamatan_current_user: null, kelurahan_current_user: null });
        setSelectedDistrict(null);
    };

    const handleDistrictChange = (value: string) => {
        setSelectedDistrict(value);
        form.setFieldsValue({ kelurahan_current_user: null });
    };

    const handleVillageChange = (value: string) => {
        const selectedVillage = villages?.find(vill => vill.kode_kelurahan === value);
        if (selectedVillage) {
            form.setFieldsValue({ kode_pos_current_user: selectedVillage.kode_pos });
        }
    };

    const handleProvinceChangeBilling = (value: string) => {
        setSelectedProvinceBilling(value);
        form.setFieldsValue({ kabupaten_billing: null, kecamatan_billing: null, kelurahan_billing: null, kode_pos_billing: null });
        setSelectedRegencyBilling(null);
        setSelectedDistrictBilling(null);
    };

    const handleRegencyChangeBilling = (value: string) => {
        setSelectedRegencyBilling(value);
        form.setFieldsValue({ kecamatan_billing: null, kelurahan_billing: null, kode_pos_billing: null });
        setSelectedDistrictBilling(null);
    };

    const handleDistrictChangeBilling = (value: string) => {
        setSelectedDistrictBilling(value);
        form.setFieldsValue({ kelurahan_billing: null, kode_pos_billing: null });
    };

    const handleVillageChangeBilling = (value: string) => {
        const selectedVillage = villagesBilling?.find(vill => vill.kode_kelurahan === value);
        if (selectedVillage) {
            form.setFieldsValue({ kode_pos_billing: selectedVillage.kode_pos });
        }
    };

    const handleProvinceChangeStnk = (value: string) => {
        setSelectedProvinceStnk(value);
        form.setFieldsValue({ kabupaten_stnk: null, kecamatan_stnk: null, kelurahan_stnk: null, kode_pos_stnk: null });
        setSelectedRegencyStnk(null);
        setSelectedDistrictStnk(null);
    };

    const handleRegencyChangeStnk = (value: string) => {
        setSelectedRegencyStnk(value);
        form.setFieldsValue({ kecamatan_stnk: null, kelurahan_stnk: null, kode_pos_stnk: null });
        setSelectedDistrictStnk(null);
    };

    const handleDistrictChangeStnk = (value: string) => {
        setSelectedDistrictStnk(value);
        form.setFieldsValue({ kelurahan_stnk: null, kode_pos_stnk: null });
    };

    const handleVillageChangeStnk = (value: string) => {
        const selectedVillage = villagesStnk?.find(vill => vill.kode_kelurahan === value);
        if (selectedVillage) {
            form.setFieldsValue({ kode_pos_stnk: selectedVillage.kode_pos });
            handleFormChange(
                { kode_pos_stnk: selectedVillage.kode_pos },
                form.getFieldsValue()
            );
        }
    };

    const handleCustomerSelect = (customer: CustomerDataType) => {
        const address = `${customer.address_line1 || ''} ${customer.address_line2 || ''}`.trim();

        if (modalTarget === 'currentUser') {
            form.setFieldsValue({
                customer_name_current_user: customer.cs_name,
                no_id_current_user: customer.id_no,
                address_current_user: address,
                provinsi_current_user: customer.provinsi,
                kabupaten_current_user: customer.kabupaten,
                kecamatan_current_user: customer.kecamatan,
                kelurahan_current_user: customer.kelurahan,
                kode_pos_current_user: customer.kode_pos,
                phone_number_current_user: customer.no_hp1,
            });

            if (customer.provinsi) {
                setSelectedProvince(customer.provinsi);
            }
            if (customer.kabupaten) {
                setSelectedRegency(customer.kabupaten);
            }
            if (customer.kecamatan) {
                setSelectedDistrict(customer.kecamatan);
            }
        } else if (modalTarget === 'billing') {
            form.setFieldsValue({
                customer_name_billing: customer.cs_name,
                no_id_billing: customer.id_no,
                address_billing: address,
                provinsi_billing: customer.provinsi,
                kabupaten_billing: customer.kabupaten,
                kecamatan_billing: customer.kecamatan,
                kelurahan_billing: customer.kelurahan,
                kode_pos_billing: customer.kode_pos,
                phone_number_billing: customer.no_hp1,
            });

            if (customer.provinsi) {
                setSelectedProvinceBilling(customer.provinsi);
            }
            if (customer.kabupaten) {
                setSelectedRegencyBilling(customer.kabupaten);
            }
            if (customer.kecamatan) {
                setSelectedDistrictBilling(customer.kecamatan);
            }
        }

        if (autosaveStatus === 'stopped') {
            handleFormChange({}, form.getFieldsValue());
        }

        handleCancel();
    };

    return {
        form,
        isModalOpen,
        showModal,
        handleOk,
        handleCancel,
        brands,
        isBrandsLoading,
        models,
        isModelsLoading,
        provinces,
        isProvincesLoading,
        regencies,
        isRegenciesLoading,
        districts,
        isDistrictsLoading,
        villages,
        isVillagesLoading,
        regenciesBilling,
        isRegenciesLoadingBilling,
        districtsBilling,
        isDistrictsLoadingBilling,
        villagesBilling,
        isVillagesLoadingBilling,
        regenciesStnk,
        isRegenciesLoadingStnk,
        districtsStnk,
        isDistrictsLoadingStnk,
        villagesStnk,
        isVillagesLoadingStnk,
        handleProvinceChange,
        handleRegencyChange,
        handleDistrictChange,
        handleVillageChange,
        handleProvinceChangeBilling,
        handleRegencyChangeBilling,
        handleDistrictChangeBilling,
        handleVillageChangeBilling,
        handleProvinceChangeStnk,
        handleRegencyChangeStnk,
        handleDistrictChangeStnk,
        handleVillageChangeStnk,
        selectedProvince,
        selectedRegency,
        selectedDistrict,
        selectedProvinceBilling,
        selectedRegencyBilling,
        selectedDistrictBilling,
        selectedProvinceStnk,
        selectedRegencyStnk,
        selectedDistrictStnk,
        handleCustomerSelect,
        autosaveStatus,
        handleFormChange,
        handleDeactivateToggle,
        showStnk,
        handleShowStnkToggle,
        showCurrentUser,
        handleShowCurrentUserToggle,
        showBilling,
        handleShowBillingToggle,
    };
};
